import React, { useMemo, useState } from 'react';
import { Alert, Card, Col, DatePicker, Row, Space, Spin, Statistic, Table, Typography } from 'antd';
import { Column } from '@ant-design/charts';
import dayjs, { Dayjs } from 'dayjs';
import { useAgentLLMUsage } from '../../../../hooks/useAgentLLMUsage';
import { AgentUsageBucket } from '../../../../services/agentUsageService';

const { Text } = Typography;
const { RangePicker } = DatePicker;

interface AgentUsageMetricsTabProps {
    chatbotId: string | undefined;
}

const DATE_FORMAT = 'YYYY-MM-DD';

function defaultRange(): [Dayjs, Dayjs] {
    // Use local dayjs (no UTC plugin dependency); the backend treats date strings as
    // calendar days, so the off-by-one risk at midnight is acceptable for a usage widget.
    const today = dayjs();
    return [today.startOf('month'), today];
}

function formatUsd(value: number): string {
    if (value === 0) return '$0.00';
    if (value < 0.01) return `$${value.toFixed(6)}`;
    return `$${value.toFixed(2)}`;
}

function formatTokens(value: number): string {
    return value.toLocaleString('en-US');
}

/**
 * Per-agent LLM usage tab. Shows total spend / tokens / call count for the selected
 * range, a daily column chart of USD cost, and a per-model breakdown table.
 *
 * Range defaults to the current calendar month (UTC). Backend caps the range at 92 days.
 */
export const AgentUsageMetricsTab: React.FC<AgentUsageMetricsTabProps> = ({ chatbotId }) => {
    const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
    const from = range[0]?.format(DATE_FORMAT);
    const to = range[1]?.format(DATE_FORMAT);

    const { data, loading, error } = useAgentLLMUsage(chatbotId, from, to);

    const dailySeries = useMemo(() => {
        if (!data) return [] as Array<{ date: string; costUsd: number }>;
        const byDate = new Map<string, number>();
        for (const bucket of data.buckets) {
            byDate.set(bucket.date, (byDate.get(bucket.date) ?? 0) + bucket.costUsd);
        }
        return Array.from(byDate.entries())
            .map(([date, costUsd]) => ({ date, costUsd: Math.round(costUsd * 1e6) / 1e6 }))
            .sort((a, b) => a.date.localeCompare(b.date));
    }, [data]);

    const modelRows = useMemo(() => {
        if (!data) return [] as AgentUsageBucket[];
        const byModel = new Map<string, AgentUsageBucket>();
        for (const bucket of data.buckets) {
            const existing = byModel.get(bucket.model);
            if (existing) {
                existing.promptTokens += bucket.promptTokens;
                existing.completionTokens += bucket.completionTokens;
                existing.totalTokens += bucket.totalTokens;
                existing.costUsd += bucket.costUsd;
                existing.callCount += bucket.callCount;
            } else {
                byModel.set(bucket.model, { ...bucket });
            }
        }
        return Array.from(byModel.values()).sort((a, b) => b.costUsd - a.costUsd);
    }, [data]);

    if (!chatbotId) {
        return <Alert type="info" message="Selecciona un agente para ver su consumo de LLM." />;
    }

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" title="Rango de fechas">
                <RangePicker
                    value={range}
                    allowClear={false}
                    onChange={(value) => {
                        if (value && value[0] && value[1]) {
                            setRange([value[0], value[1]]);
                        }
                    }}
                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Rango máximo: 92 días. Las filas se conservan 90 días en la tabla.
                </Text>
            </Card>

            {error && <Alert type="error" message={error} showIcon />}

            <Spin spinning={loading}>
                <Row gutter={16}>
                    <Col xs={24} sm={8}>
                        <Card size="small">
                            <Statistic
                                title="Costo total (USD)"
                                value={data?.totalCostUsd ?? 0}
                                precision={data && data.totalCostUsd < 0.01 ? 6 : 2}
                                prefix="$"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card size="small">
                            <Statistic title="Tokens consumidos" value={data?.totalTokens ?? 0} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card size="small">
                            <Statistic title="Llamadas al modelo" value={data?.totalCalls ?? 0} />
                        </Card>
                    </Col>
                </Row>

                <Card size="small" title="Costo diario (USD)" style={{ marginTop: 16 }}>
                    {dailySeries.length === 0 ? (
                        <Text type="secondary">Sin datos para el rango seleccionado.</Text>
                    ) : (
                        <Column
                            data={dailySeries}
                            xField="date"
                            yField="costUsd"
                            height={260}
                            xAxis={{ label: { autoRotate: true } }}
                            yAxis={{ label: { formatter: (v: string) => formatUsd(Number(v)) } }}
                            tooltip={{ formatter: (item: any) => ({ name: 'Costo', value: formatUsd(item.costUsd) }) }}
                            columnStyle={{ radius: [4, 4, 0, 0] }}
                        />
                    )}
                </Card>

                <Card size="small" title="Desglose por modelo" style={{ marginTop: 16 }}>
                    <Table<AgentUsageBucket>
                        rowKey="model"
                        dataSource={modelRows}
                        pagination={false}
                        size="small"
                        columns={[
                            { title: 'Modelo', dataIndex: 'model', key: 'model' },
                            {
                                title: 'Llamadas',
                                dataIndex: 'callCount',
                                key: 'callCount',
                                align: 'right',
                                render: (value: number) => formatTokens(value),
                            },
                            {
                                title: 'Tokens entrada',
                                dataIndex: 'promptTokens',
                                key: 'promptTokens',
                                align: 'right',
                                render: (value: number) => formatTokens(value),
                            },
                            {
                                title: 'Tokens salida',
                                dataIndex: 'completionTokens',
                                key: 'completionTokens',
                                align: 'right',
                                render: (value: number) => formatTokens(value),
                            },
                            {
                                title: 'Costo (USD)',
                                dataIndex: 'costUsd',
                                key: 'costUsd',
                                align: 'right',
                                render: (value: number) => formatUsd(value),
                            },
                        ]}
                        locale={{ emptyText: 'Sin datos para el rango seleccionado.' }}
                    />
                </Card>
            </Spin>

            <Alert
                type="info"
                showIcon
                message="Embeddings y base vectorial no están incluidos en este medidor."
                description="El cálculo cubre conversaciones que pasan por LiteLLM. El gasto de OpenAI Embeddings (base de conocimiento) no se contabiliza aquí — se hará en una fase posterior."
            />
        </Space>
    );
};
